import { useEffect, useMemo } from "react";
import { Application } from "@/types/application";
import { EntityKey } from "@/types/form-builder";
import {
  InterpreterEntities,
  useInterpreterStore,
} from "@coltorapps/builder-react";
import { entitiesComponents } from "@/const/form-builder";
import { basicFormBuilder } from "./form/builder/definition";
import { usePreloadMasterDataForExpressions } from "@/hooks/useMasterData";
import { useSetAtom } from "jotai";
import { runtimeApplicationAtom } from "@/store";

const isSameRuntimeApplication = (
  current: Application | undefined,
  next: Application,
) =>
  current?.serialNumber === next.serialNumber &&
  current?.submittedAt === next.submittedAt &&
  current?.overallStatus === next.overallStatus &&
  current?.reviewStatus === next.reviewStatus &&
  current?.applicantId === next.applicantId &&
  current?.submitterId === next.submitterId &&
  current?.applicant?.id === next.applicant?.id &&
  current?.formInstance.data === next.formInstance.data &&
  current?.formInstance.form.schema === next.formInstance.form.schema;

export default function ReadonlyForm(application: Application) {
  const { formInstance } = application;
  const { form } = formInstance;
  const setRuntimeApplication = useSetAtom(runtimeApplicationAtom);

  // const builderStore = useBuilderStore(basicFormBuilder, {
  //   initialData: formInstance.form.schema,
  // });

  console.debug({ formInstance });
  const readonlySchema = useMemo(() => {
    const entities = Object.fromEntries(
      Object.entries(form.schema.entities ?? {}).map(([entityId, entity]) => {
        const current = entity as typeof entity & {
          attributes?: Record<string, unknown>;
          type?: string;
        };
        const attributes = current.attributes ?? {};
        const shouldForceReadonly =
          "readonly" in attributes || current.type === EntityKey.grid;
        const nextAttributes =
          shouldForceReadonly
            ? {
                ...attributes,
                readonly: true,
              }
            : attributes;

        return [
          entityId,
          {
            ...current,
            attributes: nextAttributes,
          },
        ];
      }),
    ) as typeof form.schema.entities;

    return {
      ...form.schema,
      entities,
    } as typeof form.schema;
  }, [form.schema]);

  usePreloadMasterDataForExpressions(readonlySchema);

  const interpreterStore = useInterpreterStore(basicFormBuilder, readonlySchema);

  useEffect(() => {
    interpreterStore.setData({
      entitiesValues: formInstance.data,
      entitiesErrors: {},
    });
  }, [formInstance.data, interpreterStore]);

  useEffect(() => {
    setRuntimeApplication((current) =>
      isSameRuntimeApplication(current, application) ? current : application,
    );
  }, [application, setRuntimeApplication]);

  return (
    <dl className="flex flex-col gap-6">
      <InterpreterEntities
        interpreterStore={interpreterStore}
        components={entitiesComponents}
      />
    </dl>
  );
}
